import { Component, computed, effect, inject, input, output, signal } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { ProductCardWithQuantityComponent } from "@components/product-card-with-quantity/product-card-with-quantity.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { Product, ProductPropertyDimension, ProductPropertyText } from "@domain/product/product";
import { ProductAsset } from "@domain/product/product-asset";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { GasWarningCenterType } from "@project/gas-warning-center-configuration/gas-warning-center-type";
import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { Regard3000Service } from "@project/gas-warning-center-configuration/regard-3000/regard-3000.service";
import { ProductItem } from "@project/product-item";
import { filter, Subscription } from "rxjs";
import { take } from "rxjs/operators";

export interface BaseUnit {
  product: Product;
  dimensions?: ProductPropertyDimension;
  powerSupply?: ProductPropertyText;
  degreeProtection?: ProductPropertyText;
}

@Component({
  selector: "app-base-unit-selector",
  imports: [
    UiKitModule,
    ReactiveFormsModule,
    FilterProductComponent,
    ProductCardWithQuantityComponent,
    RefreshProductsHintComponent,
  ],
  templateUrl: "./base-unit-selector.component.html",
  styleUrl: "./base-unit-selector.component.scss",
  standalone: true,
})
export class BaseUnitSelectorComponent {
  readonly type = input<GasWarningCenterType>();
  readonly originalBaseUnits = input<ProductItem[]>();
  readonly selectedInputModules = input<ProductWithQuantity[]>();
  readonly selectedRelayModules = input<ProductWithQuantity[]>();
  readonly selectedGatewayModules = input<ProductWithQuantity[]>();
  readonly showCosts = input.required<boolean>();
  readonly searchQuery = signal<string>("");
  readonly selectionMode: "single" | "multi" = "multi";
  readonly isBaseUnitSelectionValid = output<boolean>();
  readonly selectedBaseUnitsChange = output<ProductWithQuantity[]>();

  readonly filteredBaseUnits = computed(() => {
    const list = this.baseUnits();
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) return list;

    return list.filter((baseUnit) => {
      const name = baseUnit.product.name?.toLowerCase() ?? "";
      const id = String(baseUnit.product.id ?? "").toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  });

  readonly baseUnitSelectionValid = computed(() => {
    const selectedBaseUnits = this.selectedBaseUnits();
    if (selectedBaseUnits.length === 0) return false;

    const count = selectedBaseUnits.reduce((sum, unit) => sum + unit.quantity, 0);

    const result = count >= this.requiredBaseUnitQuantity();
    return result;
  });

  readonly requiredBaseUnitQuantity = computed(() => {
    const modules = [this.selectedInputModules(), this.selectedRelayModules(), this.selectedGatewayModules()] as const;

    const counts = modules.map((module) => (module?.length ? this.regard3000Service.sumOfQuantity(module) : 0));
    this.assignedSlotCount = counts.reduce((sum, count) => sum + count, 0);
    const allEmpty = counts.every((c) => c === 0);
    return allEmpty ? 1 : Math.max(...counts);
  });
  readonly slotCoversChange = output<ProductWithQuantity | undefined>();

  protected readonly userTouched = signal(false);
  protected readonly selectedBaseUnits = signal<ProductWithQuantity[]>([]);
  protected readonly baseUnits = signal<BaseUnit[]>([]);
  protected readonly fallbackImageUrl = computed(() => {
    const cantoImageId = this.type()?.cantoImageId;
    return cantoImageId ? ProductImage.getCantoSourceUrl(cantoImageId) : undefined;
  });
  protected readonly productInfoUrl = computed(() => {
    const cantoId = this.filteredBaseUnits()[0]?.product.cantoProductInfoId;
    return cantoId ? ProductAsset.getCantoSourceUrl(cantoId) : undefined;
  });

  private regard3000Service = inject(Regard3000Service);
  private productService = inject(ProductService);

  private readonly POWER_SUPPLY = "Power_Supply_";
  private readonly DIMENSIONS = "Dimensions_";
  private readonly DEGREE_PROTECTION = "Degree_protection_";
  private assignedSlotCount: number = 0;
  constructor() {
    effect((onCleanup) => {
      // convert ProductItem to ProductWithQuantity
      const type = this.type();
      const originalBaseUnits = this.originalBaseUnits();
      if (type && originalBaseUnits) {
        const subscriptions: Subscription[] = [];
        for (const baseUnit of originalBaseUnits) {
          subscriptions.push(
            this.productService.getProductById(baseUnit.productId).subscribe((product) => {
              if (product) {
                this.updateSelectedBaseUnits(baseUnit.quantity, product);
                this.userTouched.set(true);
              }
            }),
          );
        }
        onCleanup(() => subscriptions.forEach((subscription) => subscription.unsubscribe()));
      }
    });
    effect(() => {
      this.selectedInputModules();
      this.selectedRelayModules();
      this.selectedGatewayModules();
    });
    effect(() => {
      // Emit result of validation to parent
      const isValid = this.baseUnitSelectionValid();
      this.isBaseUnitSelectionValid.emit(isValid);
    });
    effect((onCleanup) => {
      // Fetch Regard 3000 Plus BaseUnit products
      const type = this.type();
      if (!type) return;
      this.baseUnits.set([]);
      this.selectedBaseUnits.set([]);
      this.userTouched.set(false);

      const subscription = this.regard3000Service.getBaseUnitProducts(type).subscribe((products) => {
        const uniqueProducts = [...new Map(products.map((product) => [product.id, product])).values()];
        this.baseUnits.set(uniqueProducts.map((product) => this.createBaseUnit(product)));
      });

      onCleanup(() => subscription.unsubscribe());
    });

    effect(() => {
      // Set selectedBaseUnits
      const selectedBaseUnits = this.selectedBaseUnits();
      const baseUnits = this.baseUnits();
      const requiredQuantity = this.requiredBaseUnitQuantity();
      if (!baseUnits.length || !requiredQuantity) return;

      const value: ProductWithQuantity[] = baseUnits.map((baseUnit) => {
        const selectedBaseUnit = selectedBaseUnits.find(
          (selectedBaseUnit) => selectedBaseUnit.product.id === baseUnit.product.id,
        );
        return {
          product: baseUnit.product,
          quantity: this.userTouched() ? (selectedBaseUnit?.quantity ?? 0) : requiredQuantity,
        };
      });
      const selectedValue = this.userTouched() ? value.filter((item) => item.quantity > 0) : [];
      this.selectedBaseUnitsChange.emit(selectedValue);
      this.updateRequiredSlotCovers(selectedValue);
    });
  }

  protected updateSelectedBaseUnits(quantity: number, selectedBaseUnit: Product): void {
    if (!selectedBaseUnit?.id) return;

    this.selectedBaseUnits.update((list) => {
      const index = list.findIndex((baseUnit) => baseUnit.product.id === selectedBaseUnit.id);

      if (quantity <= 0) {
        if (index === -1) return list;
        return list.filter((_, i) => i !== index);
      }

      if (index === -1) {
        return [...list, { product: selectedBaseUnit, quantity: Math.floor(quantity) }];
      }

      const updated = [...list];
      updated[index] = { ...updated[index], quantity: Math.floor(quantity) };
      return updated;
    });
  }

  protected onBaseUnitSelectionChange(quantity: number, baseUnit: Product) {
    if (!baseUnit) return;
    this.userTouched.set(true);
    this.updateSelectedBaseUnits(quantity, baseUnit);
    const updated = this.selectedBaseUnits();
    this.selectedBaseUnitsChange.emit(updated);
    this.updateRequiredSlotCovers(updated);
  }

  protected getSelectedBaseUnitQuantity(baseUnit: Product) {
    return this.selectedBaseUnits().find((selectedBaseUnit) => selectedBaseUnit.product.id === baseUnit.id)?.quantity ?? 0;
  }

  protected getBaseUnitImageUrl(baseUnit: Product): string | undefined {
    return baseUnit.cantoImageId ? ProductImage.getCantoSourceUrl(baseUnit.cantoImageId) : this.fallbackImageUrl();
  }

  protected search(query: string) {
    this.searchQuery.set(query ?? "");
  }

  private getPowerSupplyProperty(baseUnit: Product): ProductPropertyText | undefined {
    return baseUnit.getTextPropertyByKey(this.POWER_SUPPLY);
  }

  private getDegreeprotectionProperty(baseUnit: Product): ProductPropertyText | undefined {
    return baseUnit.getTextPropertyByKey(this.DEGREE_PROTECTION);
  }

  private getDimensionsProperty(baseUnit: Product): ProductPropertyDimension | undefined {
    return baseUnit.getDimensionsPropertyByKey(this.DIMENSIONS);
  }

  private sumOfSlots(): number {
    let count = 0;
    for (const baseUnit of this.selectedBaseUnits()) {
      count += baseUnit.quantity;
    }
    return count * 3;
  }

  private sumOfRequiredSlotCovers(sumOfSlots: number) {
    return sumOfSlots - this.assignedSlotCount;
  }

  private updateRequiredSlotCovers(selectedBaseUnits: ProductWithQuantity[]) {
    if (!selectedBaseUnits.length) return;
    const baseUnit = selectedBaseUnits[0].product;

    this.regard3000Service
      .getSlotCoverProduct(baseUnit)
      .pipe(
        filter((p): p is Product => !!p),
        take(1),
      )
      .subscribe((slotCoverProduct) => {
        if (!slotCoverProduct) return;
        const slotCount = this.sumOfSlots();
        const openSlotsCount = this.sumOfRequiredSlotCovers(slotCount);
        const value: ProductWithQuantity = { product: slotCoverProduct, quantity: openSlotsCount };
        this.slotCoversChange.emit(value);
      });
  }

  private createBaseUnit(product: Product): BaseUnit {
    const dimensions = this.getDimensionsProperty(product);
    const powerSupply = this.getPowerSupplyProperty(product);
    const degreeProtection = this.getDegreeprotectionProperty(product);
    return {
      product,
      dimensions,
      powerSupply,
      degreeProtection,
    };
  }
}
